import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

/**
 * Converts 24-hour format (HH:MM) to 12-hour format
 * @param {string} time24 - Time in 24-hour format (e.g., "13:30")
 * @returns {object} - { hour: 1-12, minute: 00-59, ampm: 'AM' | 'PM' }
 */
const convert24To12 = (time24) => {
  if (!time24) {
    return { hour: 12, minute: '00', ampm: 'AM' };
  }

  const [hours, minutes] = time24.split(':').map(Number);
  const hour12 = hours % 12 || 12;
  const ampm = hours >= 12 ? 'PM' : 'AM';

  return {
    hour: hour12,
    minute: String(minutes || 0).padStart(2, '0'),
    ampm,
  };
};

/**
 * Converts 12-hour format to 24-hour format (HH:MM)
 * @param {number} hour - Hour in 12-hour format (1-12)
 * @param {string} minute - Minute (00-59)
 * @param {string} ampm - 'AM' or 'PM'
 * @returns {string} - Time in 24-hour format (e.g., "13:30")
 */
const convert12To24 = (hour, minute, ampm) => {
  let hour24 = hour;
  if (ampm === 'PM' && hour !== 12) {
    hour24 = hour + 12;
  } else if (ampm === 'AM' && hour === 12) {
    hour24 = 0;
  }
  return `${String(hour24).padStart(2, '0')}:${minute}`;
};

export function TimePicker12H({ value, onChange, className }) {
  const { hour, minute, ampm } = convert24To12(value);

  const handleHourChange = (newHour) => {
    const hourNum = parseInt(newHour, 10);
    const newTime24 = convert12To24(hourNum, minute, ampm);
    onChange(newTime24);
  };

  const handleMinuteChange = (newMinute) => {
    const minuteStr = newMinute.padStart(2, '0');
    const newTime24 = convert12To24(hour, minuteStr, ampm);
    onChange(newTime24);
  };

  const handleAmPmChange = (newAmPm) => {
    const newTime24 = convert12To24(hour, minute, newAmPm);
    onChange(newTime24);
  };

  // Generate hour options (1-12)
  const hourOptions = Array.from({ length: 12 }, (_, i) => i + 1);

  // Generate minute options (00-59, step of 1)
  const minuteOptions = Array.from({ length: 60 }, (_, i) =>
    String(i).padStart(2, '0')
  );

  return (
    <div
      className={`flex justify-between items-start gap-1.5 ${className || ''}`}
    >
      {/* Hour Selector */}
      <Select value={String(hour)} onValueChange={handleHourChange}>
        <SelectTrigger style={{ width: '66px' }}>
          <SelectValue placeholder="Hour" />
        </SelectTrigger>
        <SelectContent
          className="!max-h-[200px] !w-[80px] !min-w-[80px] [&>div:nth-child(2)]:!h-auto [&>div:nth-child(2)]:!max-h-[180px]"
          style={{ maxHeight: '200px', width: '66px', minWidth: '66px' }}
        >
          {hourOptions.map((h) => (
            <SelectItem key={h} value={String(h)}>
              {h}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Minute Selector */}
      <Select value={minute} onValueChange={handleMinuteChange}>
        <SelectTrigger style={{ width: '66px' }}>
          <SelectValue placeholder="MM" />
        </SelectTrigger>
        <SelectContent
          className="!max-h-[200px] !w-[80px] !min-w-[80px] [&>div:nth-child(2)]:!h-auto [&>div:nth-child(2)]:!max-h-[180px]"
          style={{ maxHeight: '200px', width: '66px', minWidth: '66px' }}
        >
          {minuteOptions.map((m) => (
            <SelectItem key={m} value={m}>
              {m}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* AM/PM Selector */}
      <Select value={ampm} onValueChange={handleAmPmChange}>
        <SelectTrigger style={{ width: '72px' }}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent
          className="!max-h-[200px] !w-[80px] !min-w-[80px] [&>div:nth-child(2)]:!h-auto [&>div:nth-child(2)]:!max-h-[180px]"
          style={{ maxHeight: '200px', width: '66px', minWidth: '66px' }}
        >
          <SelectItem value="AM">AM</SelectItem>
          <SelectItem value="PM">PM</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}
